import { CHART_FONT_FAMILY_CSS } from './formatters';

/** G2 tooltip host class (see @antv/g2 `G2_CLASS_PREFIX` + `tooltip`). */
const G2_TOOLTIP_CONTAINER = '.g2-tooltip';

/** Minimal shape G2 passes to interaction.tooltip.render */
export type AggregatedTooltipG2Item = {
    name?: string;
    value?: string;
    color?: string;
};

export function escapeHtmlForChartTooltip(text: string): string {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function markerColor(color: unknown, fallback: string): string {
    if (typeof color === 'string' && color.trim()) return color.trim();
    return fallback;
}

/**
 * G2 v5 view option fragment: `interaction.tooltip` for one tooltip per x (shared series)
 * with Ask Asper / Figma styling (white card, left caret, wrapped labels, colored bullets).
 *
 * Custom `render` sets innerHTML only — the `.g2-tooltip` host still gets default AntV styles
 * (off-white fill, padding, shadow). We clear those via `css` so a single card is visible.
 */
export function getAggregatedChartTooltipInteraction(): {
    shared: boolean;
    series: boolean;
    groupName: boolean;
    /** Canvas markers at each series point — disable so only the HTML tooltip shows (avoids “double pointer” confusion). */
    marker: boolean;
    single: boolean;
    css: Record<string, Record<string, string | number>>;
    render: (
        event: unknown,
        tooltipData: { title?: string; items?: AggregatedTooltipG2Item[] },
    ) => string;
} {
    return {
        shared: true,
        series: true,
        groupName: false,
        // G2 series tooltip draws Circle markers on the plot by default; keep hover feedback to the HTML card only.
        marker: false,
        single: true,
        css: {
            [G2_TOOLTIP_CONTAINER]: {
                background: 'transparent',
                'background-color': 'transparent',
                padding: '0',
                margin: '0',
                'box-shadow': 'none',
                'border-radius': '0',
                border: 'none',
                'min-width': '0',
                'max-width': 'none',
                color: 'inherit',
                'font-size': 'inherit',
                'line-height': 'inherit',
            },
        },
        render: (_event, { title, items }) => {
            const safeTitle = escapeHtmlForChartTooltip(title ?? '');
            const list = items ?? [];
            const rows = list
                .filter((it) => it && (it.name != null || it.value != null))
                .map((it) => {
                    const name = escapeHtmlForChartTooltip(String(it.name ?? ''));
                    const value = escapeHtmlForChartTooltip(String(it.value ?? ''));
                    const dot = markerColor(it.color, '#5B50D1');
                    return `<div style="display:flex;align-items:flex-start;gap:8px;width:100%;">
  <div style="flex:1;min-width:0;display:flex;gap:4px;align-items:flex-start;">
    <span style="flex-shrink:0;width:8px;height:8px;border-radius:50%;background:${dot};margin-top:7px;"></span>
    <span style="flex:1;min-width:0;color:#666666;font-size:14px;line-height:22px;font-family:${CHART_FONT_FAMILY_CSS};word-wrap:break-word;overflow-wrap:break-word;">${name}</span>
  </div>
  <span style="flex-shrink:0;color:#000000;font-size:14px;line-height:22px;font-family:${CHART_FONT_FAMILY_CSS};text-align:right;white-space:nowrap;margin-left:8px;">${value}</span>
</div>`;
                })
                .join('');

            // One card + box-shadow; caret is unfilled CSS triangle (no second drop-shadow layer).
            return `<div style="position:relative;margin-left:8px;max-width:min(360px, 80vw);">
  <div style="position:absolute;left:0;top:50%;transform:translate(-100%,-50%);width:0;height:0;border-top:6px solid transparent;border-bottom:6px solid transparent;border-right:6px solid #ffffff;"></div>
  <div style="background:#ffffff;border-radius:6px;padding:6px 8px;min-width:120px;box-sizing:border-box;box-shadow:0 2px 8px rgba(0,0,0,0.12);display:flex;flex-direction:column;gap:8px;">
    <div style="color:#666666;font-size:14px;line-height:22px;font-family:${CHART_FONT_FAMILY_CSS};word-wrap:break-word;">${safeTitle}</div>
    ${rows ? `<div style="display:flex;flex-direction:column;gap:8px;">${rows}</div>` : ''}
  </div>
</div>`;
        },
    };
}

/**
 * Pale vertical band behind the active x/category (G2 `elementHighlightByX` + `region` + `background`).
 * Works best for interval/point marks; line-only views need point marks for x-band hit testing.
 */
export function getChartColumnHighlightInteraction(): {
    elementHighlightByX: {
        region: boolean;
        background: boolean;
    };
} {
    return {
        elementHighlightByX: {
            /** Use x-position (nearest category) in the plot, not only the bar under the cursor. */
            region: true,
            /** Draws the semi-transparent band via `renderBackground` (default fill ~#CCD6EC @ 0.3). */
            background: true,
        },
    };
}
